import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
} from 'victory';

import type { MultiLineChartProps } from './types';

export function MultiLineChart<T extends { date: string }>({
  data,
  series,
  yAxisLabel = '',
  height = 380,
  yTickValues = [0, 2, 4, 6, 8, 10],
  yMax = 10,
  yMin = 0,
  lineInterpolation = 'natural',
  showDataPointMarkers = false,
  dataPointMarkerSize = 4,
  formatYTick,
  formatEndLabel,
  formatTooltipValue,
  preserveLegendOrder = false,
}: MultiLineChartProps<T>) {
  const { t } = useTranslation('label-perf');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [cursorX, setCursorX] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(1200);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Track container width for responsive chart
  useEffect(() => {
    const container = chartContainerRef.current;

    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    setContainerWidth(container.offsetWidth);

    return () => resizeObserver.disconnect();
  }, []);

  // Get last values for end labels and sorting
  const lastValues = useMemo(() => {
    const values: Record<string, number> = {};

    if (data.length === 0) {
      series.forEach((s) => {
        values[s.key] = 0;
      });

      return values;
    }

    const lastDataPoint = data[data.length - 1] as Record<string, unknown>;

    series.forEach((s) => {
      values[s.key] = (lastDataPoint[s.key] as number) || 0;
    });

    return values;
  }, [data, series]);

  // Series sorted by latest value (largest first) - used for chart lines and end labels
  const sortedSeries = useMemo(() => {
    return [...series].sort((a, b) => lastValues[b.key] - lastValues[a.key]);
  }, [series, lastValues]);

  // Series for legend display - preserve original order if requested
  const legendSeries = useMemo(() => {
    return preserveLegendOrder ? series : sortedSeries;
  }, [series, sortedSeries, preserveLegendOrder]);

  // Prepare chart series data
  const chartSeries = useMemo(() => {
    return sortedSeries.map((s) => ({
      ...s,
      data: data.map((d, index) => ({
        x: index,
        y: ((d as Record<string, unknown>)[s.key] as number) || 0,
      })),
    }));
  }, [data, sortedSeries]);

  // Chart dimensions
  const chartWidth = containerWidth;

  // Responsive padding
  const chartPadding = useMemo(() => {
    if (containerWidth < 600) {
      return { top: 20, bottom: 55, left: 55, right: 50 };
    }

    return { top: 20, bottom: 60, left: 70, right: 65 };
  }, [containerWidth]);

  // Get tick indices for x-axis - fewer ticks on smaller screens
  const xTickIndices = useMemo(() => {
    const indices: number[] = [];
    const dataLength = data.length;

    let tickInterval: number;

    if (containerWidth < 500) {
      tickInterval = 8;
    } else if (containerWidth < 700) {
      tickInterval = 6;
    } else if (containerWidth < 900) {
      tickInterval = 4;
    } else {
      tickInterval = 2;
    }

    if (dataLength === 0) {
      return indices;
    }

    for (let i = 0; i < dataLength; i += tickInterval) {
      indices.push(i);
    }

    const lastIndex = dataLength - 1;

    if (indices[indices.length - 1] !== lastIndex) {
      indices.push(lastIndex);
    }

    return indices;
  }, [containerWidth, data.length]);

  const formatXTick = (index: number) => {
    if (index >= 0 && index < data.length) {
      return data[index].date;
    }

    return '';
  };

  // Format number with appropriate suffix (T, B, M, K)
  const formatWithSuffix = (value: number, decimals = 1): string => {
    const absValue = Math.abs(value);

    if (absValue >= 1000) {
      return (value / 1000).toFixed(decimals) + 'T';
    }

    if (absValue >= 1) {
      return value.toFixed(decimals) + 'B';
    }

    if (absValue >= 0.001) {
      return (value * 1000).toFixed(decimals) + 'M';
    }

    if (absValue >= 0.000001) {
      return (value * 1000000).toFixed(decimals) + 'K';
    }

    return value.toFixed(decimals);
  };

  // Default end label formatter
  const defaultFormatEndLabel = (value: number): string => {
    return formatWithSuffix(value, 1);
  };

  // Default tooltip value formatter
  const defaultFormatTooltipValue = (value: number): string => {
    return formatWithSuffix(value, 2);
  };

  // Get the tooltip formatter to use
  const tooltipValueFormatter = formatTooltipValue ?? defaultFormatTooltipValue;

  // Prepare end label data
  const endLabelData = useMemo(() => {
    const lastIndex = data.length - 1;

    return sortedSeries.map((s) => {
      const value = lastValues[s.key];
      const labelFormatter = formatEndLabel ?? defaultFormatEndLabel;

      return {
        x: lastIndex,
        y: value,
        key: s.key,
        color: s.color,
        label: labelFormatter(value),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length, lastValues, sortedSeries, formatEndLabel]);

  // Get values at hovered index
  const hoveredData = useMemo(() => {
    if (hoveredIndex === null) {
      return null;
    }

    const dataPoint = data[hoveredIndex];

    if (!dataPoint) {
      return null;
    }

    const dataPointRecord = dataPoint as Record<string, unknown>;

    return {
      date: dataPoint.date,
      values: sortedSeries.map((s) => ({
        key: s.key,
        label: s.label,
        color: s.color,
        value: (dataPointRecord[s.key] as number) || 0,
      })),
    };
  }, [hoveredIndex, data, sortedSeries]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (data.length === 0) {
        return;
      }

      const container = chartContainerRef.current;

      if (!container) {
        return;
      }

      const svg = container.querySelector('svg');

      if (!svg) {
        return;
      }

      const point = svg.createSVGPoint();

      point.x = e.clientX;
      point.y = e.clientY;

      const ctm = svg.getScreenCTM();

      if (!ctm) {
        return;
      }

      const svgPoint = point.matrixTransform(ctm.inverse());
      const svgX = svgPoint.x;

      const chartAreaLeft = chartPadding.left;
      const chartAreaRight = chartWidth - chartPadding.right;
      const chartAreaWidth = chartAreaRight - chartAreaLeft;

      if (svgX < chartAreaLeft || svgX > chartAreaRight) {
        setHoveredIndex(null);

        return;
      }

      const containerRect = container.getBoundingClientRect();

      setCursorX(e.clientX - containerRect.left);

      const relativeX = svgX - chartAreaLeft;
      const dataLength = data.length;
      const index = Math.round((relativeX / chartAreaWidth) * (dataLength - 1));
      const clampedIndex = Math.max(0, Math.min(dataLength - 1, index));

      setHoveredIndex(clampedIndex);
    },
    [chartPadding.left, chartPadding.right, chartWidth, data],
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  if (data.length === 0) {
    return (
      <div className="rounded-sm border px-md py-lg bg-level-02 border-emphasis-subtle sm:px-xl md:px-2xl">
        <div
          className="flex items-center justify-center"
          style={{ height }}
          data-testid="chart-container"
        >
          <span className="text-body-lg text-subtle">{t('noData.chart')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-sm border px-md py-lg bg-level-02 border-emphasis-subtle sm:px-xl md:px-2xl">
      {/* Legend */}
      <div className="mb-sm flex flex-wrap items-center justify-center gap-md sm:gap-lg md:gap-xl">
        {legendSeries.map((s) => (
          <div key={s.key} className="flex items-center gap-sm">
            <div
              className="h-[3px] w-lg rounded-full"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-body-sm text-subtle">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div
        ref={chartContainerRef}
        className="relative cursor-crosshair"
        style={{ height }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        data-testid="chart-container"
      >
        <VictoryChart
          width={chartWidth}
          height={height}
          padding={chartPadding}
          domainPadding={{ y: 20 }}
          domain={{ y: [yMin, yMax] }}
        >
          {/* Y-Axis */}
          <VictoryAxis
            dependentAxis
            tickValues={yTickValues}
            tickFormat={formatYTick ?? ((v: number) => formatWithSuffix(v, 1))}
            style={{
              axis: { stroke: '#FFFFFF12' },
              grid: { stroke: '#FFFFFF12' },
              tickLabels: {
                fill: '#878787',
                fontSize: 12,
                fontFamily: 'SF Pro Text, sans-serif',
              },
              axisLabel: {
                fill: '#878787',
                fontSize: 14,
                fontFamily: 'SF Pro Text, sans-serif',
                padding: 55,
              },
            }}
            label={yAxisLabel}
          />

          {/* X-Axis */}
          <VictoryAxis
            tickValues={xTickIndices}
            tickFormat={formatXTick}
            style={{
              axis: { stroke: '#FFFFFF12' },
              tickLabels: {
                fill: '#878787',
                fontSize: 10,
                fontFamily: 'SF Pro Text, sans-serif',
                angle: -45,
                textAnchor: 'end',
              },
            }}
          />

          {/* Lines */}
          {chartSeries.map((s) => (
            <VictoryLine
              key={s.key}
              data={s.data}
              interpolation={lineInterpolation}
              style={{
                data: {
                  stroke: s.color,
                  strokeWidth: 2,
                },
              }}
            />
          ))}

          {/* Data point markers (optional) */}
          {showDataPointMarkers
            ? chartSeries.map((s) => (
                <VictoryScatter
                  key={`markers-${s.key}`}
                  data={s.data}
                  size={dataPointMarkerSize}
                  style={{
                    data: {
                      fill: s.color,
                      stroke: s.color,
                      strokeWidth: 1,
                    },
                  }}
                />
              ))
            : null}

          {/* End value labels */}
          {endLabelData.map((point) => (
            <VictoryScatter
              key={point.key}
              data={[point]}
              size={0}
              labels={({ datum }: { datum: { label: string } }) => datum.label}
              labelComponent={
                <VictoryLabel
                  dx={6}
                  dy={1}
                  textAnchor="start"
                  style={{
                    fill: point.color,
                    fontSize: 10,
                    fontFamily: 'SF Pro Text, sans-serif',
                    fontWeight: 500,
                  }}
                />
              }
            />
          ))}

          {/* Vertical hover indicator line */}
          {hoveredIndex !== null && (
            <VictoryLine
              data={[
                { x: hoveredIndex, y: yMin },
                { x: hoveredIndex, y: yMax },
              ]}
              style={{
                data: {
                  stroke: 'rgba(255, 255, 255, 0.3)',
                  strokeWidth: 1,
                  strokeDasharray: '4,2',
                },
              }}
            />
          )}

          {/* Hover dots on each line */}
          {hoveredIndex !== null &&
            chartSeries.map((s) => (
              <VictoryScatter
                key={`hover-dot-${s.key}`}
                data={[s.data[hoveredIndex]]}
                size={5}
                style={{
                  data: {
                    fill: s.color,
                    stroke: '#1a1a1a',
                    strokeWidth: 2,
                  },
                }}
              />
            ))}
        </VictoryChart>

        {/* Tooltip */}
        {hoveredIndex !== null && hoveredData ? (
          <div
            className="pointer-events-none absolute top-[20px] z-10 rounded-md border px-md py-sm shadow-lg bg-level-03 border-emphasis-subtle"
            style={
              hoveredIndex > data.length / 2
                ? { right: `calc(100% - ${cursorX}px + 12px)` }
                : { left: cursorX + 12 }
            }
          >
            <div className="mb-xs border-b pb-xs text-[11px] font-semibold border-emphasis-subtle text-default">
              {hoveredData.date}
            </div>
            <div className="flex flex-col gap-[3px]">
              {hoveredData.values.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between gap-lg text-[11px]"
                >
                  <div className="flex items-center gap-xs">
                    <div
                      className="h-[6px] w-[6px] rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-subtle">{item.label}</span>
                  </div>
                  <span className="font-medium tabular-nums text-default">
                    {tooltipValueFormatter(item.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

MultiLineChart.displayName = 'MultiLineChart';
